// @ts-nocheck
import { hasRole } from "./roles";
/**
 * Side-nav item definitions and the visibility rules that gate them.
 *
 * Deliberately free of React and react-router imports so the rules can be unit-tested directly —
 * the same reasoning as utils/accounts.ts. The one that matters most: an `employeeOnly` entry
 * is INTERNAL ONLY and a customer must never be offered it.
 */

export type NavItem = {
  path: string;
  label: string;
  icon: string;
  hideWhenPending?: boolean;
  supportOnly?: boolean;
  /** AvSight internal staff only — driven by Copilot Employee Access on the Salesforce Contact. */
  employeeOnly?: boolean;
  /**
   * Marks the entry as a beta surface, which the side nav renders as a small flask badge.
   *
   * Separate from `employeeOnly` on purpose. employeeOnly answers "may this caller see it"; `beta`
   * answers "is this thing finished". They coincide today and will not always: a beta that opens
   * up to support admins is still beta, and an internal tool that is finished stops being beta
   * without ceasing to be internal.
   */
  beta?: boolean;
};

/**
 * The Learning group — the NEW experience, for everyone (ACOP-140, David, 2026-10-13).
 *
 * Home, Copilot and the Knowledge Base are the live product: the git-backed RAG and the in-app KB,
 * not the old Azure-search page or the Salesforce-hosted iframe KB. There is no Beta Preview swap and
 * no "(beta)" suffix or flask on these three. The paths keep their `-2` names on purpose: the AVS OS
 * KB and Home content link to /knowledge-base-2?article=…, so renaming them would break those links.
 * The old /copilot and /resources/knowledge-base still resolve — they redirect (utils/legacyRoutes.ts)
 * so nobody's bookmark dies.
 *
 * The `beta` flag on NavItem stays for OTHER beta features (the LMS being embedded, for one); nothing
 * here uses it.
 */
export const PRIMARY: NavItem[] = [
  { path: "/home", label: "Home", icon: "fa-solid fa-house" },
  { path: "/copilot-2", label: "Copilot", icon: "fa-solid fa-book-open-reader" },
  { path: "/knowledge-base-2", label: "Knowledge Base", icon: "fa-solid fa-book-atlas" },
  { path: "/resources/university", label: "AvSight University", icon: "fa-solid fa-graduation-cap" },
];

export const ADDITIONAL_RESOURCES: NavItem[] = [
  {
    path: "/release-notes",
    label: "Change Log",
    icon: "fa-solid fa-clipboard-list",
  },
  {
    path: "/feature-requests",
    label: "Feature Voting",
    icon: "fa-solid fa-square-poll-vertical",
  },
];

export const ACCOUNT: NavItem[] = [
  {
    path: "/support-tickets",
    label: "Support Tickets",
    icon: "fa-solid fa-ticket-simple",
    supportOnly: true,
    hideWhenPending: true,
  },
  {
    path: "/change-requests",
    label: "Change requests",
    icon: "fa-solid fa-clipboard-list",
    supportOnly: true,
    hideWhenPending: true,
  },
  {
    path: "/upgrade-requests",
    label: "Upgrade Requests",
    icon: "fa-solid fa-circle-arrow-up",
    supportOnly: true,
    hideWhenPending: true,
  },
  {
    path: "/my-subscription",
    label: "My Subscription",
    icon: "fa-solid fa-file-signature",
    supportOnly: true,
    hideWhenPending: true,
  },
  {
    path: "/discover-addons",
    label: "Add-On Services",
    icon: "fa-solid fa-cart-plus",
    supportOnly: true,
  },
  {
    path: "/early-adopters",
    label: "Early Adopters",
    icon: "fa-solid fa-stream",
    supportOnly: true,
  },
];

/**
 * Which items a caller may see.
 *
 * Fails CLOSED: hasRole returns false for a missing, malformed or non-array roles value, so a
 * failed role refresh hides gated items rather than revealing them.
 */
export function filterItems(items: NavItem[], roles: unknown[], reqStatus: string) {
  return items.filter((item) => {
    if (item.supportOnly && !hasRole(roles, "support")) return false;
    if (item.employeeOnly && !hasRole(roles, "employee")) return false;
    if (item.hideWhenPending && reqStatus === "Requested") return false;
    return true;
  });
}
